import { searchService } from "@/modules/hub/features/Search/services/searchService";
import type {
  GetResourceSearchResultsRequest,
  GetResourceSearchResultsItem,
} from "@/api/getResourceSearchResults/types";

/**
 * Service layer for Podcasts to use Algolia
 * Transforms between Podcasts format and Algolia format
 */
class PodcastsAlgoliaService {
  /**
   * Search podcasts using Algolia
   * Returns data in the format Podcasts UI expects
   */
  async searchPodcasts(params: GetResourceSearchResultsRequest): Promise<{
    resources: GetResourceSearchResultsItem[];
    totalCount: number;
    facets: {
      categories: Array<{ id: number; name: string; count: number }>;
      businessRoles: Array<{
        id: number;
        name: string;
        description: string | null;
        count: number;
      }>;
      trades: Array<{
        id: number;
        name: string;
        icon: string | null;
        count: number;
      }>;
    };
  }> {
    // Initialize with 'podcasts' context to only show Podcast type
    await searchService.initialize(false, "podcasts");

    // Build Algolia filters from Podcasts params
    const algoliaFilters: any = {
      type: "resources", // Only search resources, not events
    };

    // Add category filters (array)
    if (params.categoryNames && params.categoryNames.length > 0) {
      algoliaFilters.categories = params.categoryNames;
    }

    // Add business role filters (array)
    if (params.businessRoleNames && params.businessRoleNames.length > 0) {
      algoliaFilters.businessRoles = params.businessRoleNames;
    }

    // Add trade filters (array)
    if (params.tradeNames && params.tradeNames.length > 0) {
      algoliaFilters.trades = params.tradeNames;
    }

    // Calculate page for Algolia (0-indexed)
    const algoliaPage = (params.page || 1) - 1;
    const hitsPerPage = params.pageSize || 10;

    // Map sortBy and sortOrder to sort option
    let sortOption = "newest"; // default
    if (params.sortBy && params.sortOrder) {
      if (params.sortBy === "publishStartDate" && params.sortOrder === "desc") {
        sortOption = "newest";
      } else if (
        params.sortBy === "publishStartDate" &&
        params.sortOrder === "asc"
      ) {
        sortOption = "oldest";
      } else if (params.sortBy === "viewCount" && params.sortOrder === "desc") {
        sortOption = "mostViewed";
      } else if (params.sortBy === "title" && params.sortOrder === "asc") {
        sortOption = "alphabetical";
      }
    }

    // Determine if we should use favorites key
    const useFavoritesKey = params.showFavorites === true;

    // Search Algolia
    const result = await searchService.searchResources(
      params.searchTerm || "", // Search term
      algoliaFilters,
      algoliaPage,
      hitsPerPage,
      sortOption,
      useFavoritesKey, // Use favorites secured key if showing favorites only
    );

    // Transform Algolia hits to Podcasts format
    const resources: GetResourceSearchResultsItem[] = result.hits.map(
      (hit: any) => ({
        id:
          hit.resourceId ||
          hit.objectID?.replace("resource_", "") ||
          Math.random() * 100000,
        uuid: hit.uuid || "",
        slug: hit.slug || "",
        title: hit.title || "",
        searchDescription: hit.searchDescription || null,
        isPublished: hit.isPublished || false,
        thumbnailUrl: hit.thumbnail || null,
        thumbnailFileUuid: null,
        primaryIcon: hit.primaryIcon || null,
        isFeatured: hit.isFeatured || false,
        resourceType: hit.resourceType || null,
        // Algolia stores timestamps in seconds, convert to ISO string
        createdOrPublishStartDate:
          hit.publishStartDate || hit.createdAt
            ? new Date(
                (hit.publishStartDate || hit.createdAt) * 1000,
              ).toISOString()
            : null,
        viewCount: hit.viewCount || 0,
        backgroundColor: hit.backgroundColor || null,
        textColor: hit.textColor || null,
        borderColor: hit.borderColor || null,
        contentUrl: hit.url || null,
        isFavorited: false, // Will be handled by usePodcasts hook
      }),
    );

    // Helper to generate consistent IDs from strings
    const stringToId = (str: string): number => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    };

    // Transform facets to Podcasts format with counts
    const facets = {
      categories: Object.entries(result.facets?.categories || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          count: count as number,
        }),
      ),
      businessRoles: Object.entries(result.facets?.businessRoles || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          description: null,
          count: count as number,
        }),
      ),
      trades: Object.entries(result.facets?.trades || {}).map(
        ([name, count]) => ({
          id: stringToId(name),
          name,
          icon: null,
          count: count as number,
        }),
      ),
    };

    return {
      resources,
      totalCount: result.total,
      facets,
    };
  }

  /**
   * Get available facets for filters from Algolia
   * Transforms Algolia facet strings into objects the UI expects
   */
  async getMetadata(): Promise<{
    categories: Array<{ id: number; name: string }>;
    businessRoles: Array<{
      id: number;
      name: string;
      description: string | null;
    }>;
    trades: Array<{ id: number; name: string; icon: string | null }>;
  }> {
    // Initialize with 'podcasts' context to only show Podcast type
    await searchService.initialize(false, "podcasts");

    // Search with empty query to get all facets for podcasts only
    const result = await searchService.searchAll(
      "",
      { type: "resources" },
      0,
      0,
    );

    // Extract facet values from Algolia response
    const facets = result.facets || {};

    // Transform facet strings to objects with mock IDs (hash of the string for consistency)
    const stringToId = (str: string): number => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    };

    return {
      categories: Object.keys(facets.categories || {}).map((name) => ({
        id: stringToId(name),
        name,
      })),
      businessRoles: Object.keys(facets.businessRoles || {}).map((label) => ({
        id: stringToId(label),
        name: label,
        description: null,
      })),
      trades: Object.keys(facets.trades || {}).map((name) => ({
        id: stringToId(name),
        name,
        icon: null,
      })),
    };
  }
}

export const podcastsAlgoliaService = new PodcastsAlgoliaService();
